import React from "react";
import { Link } from "react-router";
import {
  ArrowRight,
  ChartNoAxesCombined,
  GitBranch,
  LayoutGrid,
  MapPin,
  Package,
  Truck,
  Upload,
} from "lucide-react";
import useAccess from "../hooks/useAccess.jsx";
import PublicFeatureBanner from "./PublicFeatureBanner.jsx";
import { BRAND_LOGO_SRC, BRAND_NAME } from "./BrandLogo.jsx";
import {
  canAccessDispatchPage,
  canAccessDriverPortal,
  canAccessGeneralWorkspacePage,
  canAccessImportPage,
  isBillingWorkspaceOnly,
  resolveBillingWorkspaceEntryPath,
} from "../lib/accessControl.js";
import { enrichResultsUrlWithStoredDispatchContext } from "../utils/dispatchDateUrls.js";
import {
  buttonToneClasses,
  joinClasses,
} from "./resultsPrimitives/shared.js";
import "./AuthenticatedHomePage.css";

// Match the public landing page's asset base in Vite and in FastAPI.
const homeImageSrc = import.meta.env.DEV
  ? `${import.meta.env.BASE_URL}brand/landing/Camion%20Meunerie.png`
  : "/brand/landing/Camion%20Meunerie.png";

const homeActionClassName = "authenticated-home__action rounded-[4px] border focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent";

const dispatchLinkClassName = joinClasses([
  homeActionClassName,
  "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong",
]);

export default function AuthenticatedHomePage() {
  const { access } = useAccess();
  const canOpenDispatch = canAccessDispatchPage(access);
  const canOpenWorkspace = canAccessGeneralWorkspacePage(access);
  const hasUnavailableBillingAccess = isBillingWorkspaceOnly(access) && !resolveBillingWorkspaceEntryPath(access);
  const dispatchHref = enrichResultsUrlWithStoredDispatchContext("/results");
  const tools = [
    {
      number: "01",
      title: "Importez",
      description: "Préparez vos commandes et vos volumes.",
      label: "Import fichiers",
      href: "/import",
      icon: Upload,
      visible: canAccessImportPage(access),
    },
    {
      number: "02",
      title: "Optimisez",
      description: "Construisez des tournées adaptées à votre flotte.",
      label: "Dispatch",
      href: dispatchHref,
      icon: GitBranch,
      visible: canOpenDispatch,
    },
    {
      number: "03",
      title: "Exécutez",
      description: "Retrouvez le suivi terrain et le portail chauffeurs.",
      label: "Portail",
      href: canAccessDriverPortal(access) ? "/portail" : null,
      icon: Truck,
      visible: true,
    },
    {
      number: "04",
      title: "Analysez",
      description: "Consultez vos résultats, vos coûts et vos marges.",
      label: "Rapports",
      href: "/reports",
      icon: ChartNoAxesCombined,
      visible: canOpenWorkspace,
    },
  ].filter((tool) => tool.visible);

  return (
    <div
      className="authenticated-home"
      data-authenticated-home="true"
    >
      <div className="authenticated-home__intro">
        <section className="authenticated-home__hero" aria-labelledby="home-title">
          <img
            className="authenticated-home__landscape"
            src={homeImageSrc}
            alt=""
            width={1672}
            height={941}
            fetchpriority="high"
            decoding="async"
          />
          <div className="authenticated-home__welcome">
            <p className="authenticated-home__eyebrow text-accent">Espace opérateur</p>
            <h1
              id="home-title"
              className="text-accent-strong"
              aria-label={hasUnavailableBillingAccess ? undefined : "Bienvenue dans TransIA"}
            >
              {hasUnavailableBillingAccess ? "Accès Facturation indisponible" : (
                <>
                  Bienvenue dans{" "}
                  <span className="authenticated-home__wordmark">
                    <img src={BRAND_LOGO_SRC} alt={BRAND_NAME} width={535} height={165} decoding="async" />
                  </span>
                </>
              )}
            </h1>
            {hasUnavailableBillingAccess ? (
              <p className="authenticated-home__description">
                Ce rôle ne possède actuellement aucun droit permettant d’ouvrir la Facturation ou la Consolidation.
              </p>
            ) : (
              <>
                <p className="authenticated-home__tagline">Un transport plus performant, au quotidien.</p>
                <p className="authenticated-home__description">
                  Retrouvez vos outils pour planifier, suivre et analyser vos opérations.
                </p>
              </>
            )}
            <div className="authenticated-home__actions">
              {canOpenDispatch ? (
                <Link className={dispatchLinkClassName} to={dispatchHref}>
                  <GitBranch size={21} aria-hidden="true" />
                  Ouvrir le Dispatch
                  <ArrowRight size={19} aria-hidden="true" />
                </Link>
              ) : null}
              {canOpenWorkspace ? (
                <Link className={joinClasses([homeActionClassName, buttonToneClasses.ghost])} to="/dashboard">
                  <LayoutGrid size={21} aria-hidden="true" />
                  Tableau de bord
                </Link>
              ) : null}
            </div>
          </div>
        </section>
        <PublicFeatureBanner compact />
      </div>

      {tools.length > 0 ? (
        <section className="authenticated-home__tools" aria-labelledby="home-tools-title">
          <header className="authenticated-home__section-heading">
            <h2 id="home-tools-title">Vos outils au quotidien</h2>
            <p>De la commande à la livraison.</p>
          </header>
          <div className="authenticated-home__tool-grid">
            {tools.map(({ number, title, description, label, href, icon: Icon }) => (
              <article className="authenticated-home__tool" key={number}>
                <div className="authenticated-home__tool-top">
                  <Icon size={38} strokeWidth={1.6} className="text-accent" aria-hidden="true" />
                  <span aria-hidden="true">{number}</span>
                </div>
                <h3>{title}</h3>
                <p>{description}</p>
                {href ? (
                  <Link className="authenticated-home__text-link" to={href}>
                    {label}<ArrowRight size={19} aria-hidden="true" />
                  </Link>
                ) : (
                  <span className="authenticated-home__text-link">{label}</span>
                )}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {canOpenWorkspace ? (
        <section className="authenticated-home__reference" aria-labelledby="home-reference-title">
          <div className="authenticated-home__reference-copy">
            <h2 id="home-reference-title">Vos données de référence</h2>
            <p>Les informations essentielles à vos opérations.</p>
            <Link className="authenticated-home__text-link" to="/data-manager">
              Ouvrir Data Manager<ArrowRight size={19} aria-hidden="true" />
            </Link>
          </div>
          <nav className="authenticated-home__reference-links" aria-label="Données de référence">
            <Link to="/data-manager?tab=correspondences">
              <MapPin size={36} strokeWidth={1.5} aria-hidden="true" />
              <span>Clients et sites</span>
            </Link>
            <Link to="/data-manager?tab=vehicles">
              <Truck size={38} strokeWidth={1.5} aria-hidden="true" />
              <span>Flotte et chauffeurs</span>
            </Link>
            <Link to="/data-manager?tab=product_catalog">
              <Package size={36} strokeWidth={1.5} aria-hidden="true" />
              <span>Produits</span>
            </Link>
          </nav>
        </section>
      ) : null}

      <footer className="authenticated-home__footer">TransIA · Espace opérateur</footer>
    </div>
  );
}
